/* NEXLETE — header behaviour.
   Custom element so it survives Shopify section re-renders in the theme editor.
   Keyboard and pointer parity: a mega panel opens on hover OR focus, and Escape
   always closes. No dependencies. */

class NxHeader extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll("[data-nx-nav-item]")];
    this.burger = this.querySelector("[data-nx-burger]");
    this.drawer = this.querySelector("[data-nx-drawer]");
    this.openItem = null;
    this.closeTimer = null;

    this.items.forEach((item) => this.wireItem(item));
    this.wireDrawer();
    this.wireScroll();
    this.wireSearch();

    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closePanel();
        this.setDrawer(false);
        this.setSearch(false);
      }
    });

    // A click anywhere outside the header dismisses whatever is open.
    this.onDocClick = (e) => {
      if (!this.contains(e.target)) {
        this.closePanel();
        this.setSearch(false);
      }
    };
    document.addEventListener("click", this.onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onDocClick);
    if (this.observer) this.observer.disconnect();
  }

  /* ------------------------------------------------------------ mega panel */
  wireItem(item) {
    const link = item.querySelector(".nx-nav__link");
    const panel = item.querySelector("[data-nx-panel]");
    if (!panel) return;

    const open = () => {
      clearTimeout(this.closeTimer);
      if (this.openItem && this.openItem !== item) this.closePanel();
      panel.hidden = false;
      // Next frame, so the transition has a start state to animate from.
      requestAnimationFrame(() => item.setAttribute("data-open", "true"));
      link.setAttribute("aria-expanded", "true");
      this.setAttribute("data-open", "true");
      this.openItem = item;
    };

    // Close on a short delay so a diagonal mouse path from link to panel
    // doesn't dismiss it mid-travel.
    const scheduleClose = () => {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closePanel(), 140);
    };

    item.addEventListener("mouseenter", open);
    item.addEventListener("mouseleave", scheduleClose);
    item.addEventListener("focusin", open);
    item.addEventListener("focusout", (e) => {
      if (!item.contains(e.relatedTarget)) scheduleClose();
    });

    // Touch: first tap opens, second follows the link.
    link.addEventListener("click", (e) => {
      if (!window.matchMedia("(hover: none)").matches) return;
      if (this.openItem !== item) {
        e.preventDefault();
        open();
      }
    });
  }

  closePanel() {
    clearTimeout(this.closeTimer);
    if (!this.openItem) {
      this.removeAttribute("data-open");
      return;
    }
    const item = this.openItem;
    const panel = item.querySelector("[data-nx-panel]");
    const link = item.querySelector(".nx-nav__link");
    item.removeAttribute("data-open");
    link.setAttribute("aria-expanded", "false");
    this.removeAttribute("data-open");
    this.openItem = null;

    const hide = () => {
      if (!item.hasAttribute("data-open")) panel.hidden = true;
    };
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) hide();
    else setTimeout(hide, 280);
  }

  /* ---------------------------------------------------------------- drawer */
  wireDrawer() {
    if (!this.burger || !this.drawer) return;

    this.burger.addEventListener("click", () => {
      this.setDrawer(this.getAttribute("data-drawer") !== "true");
    });

    this.drawer.querySelectorAll("[data-nx-drawer-toggle]").forEach((btn) => {
      const panel = btn.nextElementSibling;
      btn.addEventListener("click", () => {
        const isOpen = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", String(!isOpen));
        panel.hidden = isOpen;
      });
    });
  }

  setDrawer(open) {
    if (!this.drawer) return;
    this.setAttribute("data-drawer", String(open));
    this.drawer.hidden = !open;
    this.burger?.setAttribute("aria-expanded", String(open));
    // Lock the page behind the drawer without losing scroll position.
    document.documentElement.style.overflow = open ? "hidden" : "";
  }

  /* ---------------------------------------------------------------- search
     Predictive search. Results markup comes from a Liquid section rather than
     being assembled here, so prices go through `money` and images through the
     CDN's responsive sets — /search/suggest.json gives neither. */
  wireSearch() {
    this.searchTrigger = this.querySelector("[data-nx-search-open]");
    this.searchPanel = this.querySelector("[data-nx-search-panel]");
    this.searchInput = this.querySelector("[data-nx-search-input]");
    this.searchResults = this.querySelector("[data-nx-search-results]");
    this.searchStatus = this.querySelector("[data-nx-search-status]");
    if (!this.searchTrigger || !this.searchPanel || !this.searchInput) return;

    // Keep the untouched resting markup so it can be restored when the field
    // is cleared, instead of leaving the last query's results on screen.
    this.searchRest = this.searchResults.innerHTML;
    this.searchOpts = [];
    this.searchActive = -1;
    this.searchDebounce = null;
    this.searchAbort = null;
    this.searchCache = new Map();

    this.searchTrigger.addEventListener("click", (e) => {
      e.preventDefault();
      this.setSearch(this.getAttribute("data-search") !== "true");
    });

    this.querySelector("[data-nx-search-close]")?.addEventListener("click", () =>
      this.setSearch(false)
    );

    this.searchInput.addEventListener("input", () => {
      clearTimeout(this.searchDebounce);
      // Long enough that a fast typist sends one request, not six.
      this.searchDebounce = setTimeout(() => this.runSearch(), 220);
    });

    this.searchInput.addEventListener("keydown", (e) => this.searchKeys(e));
  }

  setSearch(open) {
    if (!this.searchPanel) return;
    if (open === (this.getAttribute("data-search") === "true")) return;

    this.setAttribute("data-search", String(open));
    this.searchTrigger.setAttribute("aria-expanded", String(open));

    if (open) {
      this.closePanel();
      this.searchPanel.hidden = false;
      requestAnimationFrame(() => this.searchInput.focus());
    } else {
      this.moveActive(-1);
      const hide = () => {
        if (this.getAttribute("data-search") !== "true") this.searchPanel.hidden = true;
      };
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) hide();
      else setTimeout(hide, 240);
    }
  }

  async runSearch() {
    const q = this.searchInput.value.trim();

    // One character matches most of the catalogue; it is noise, not a result.
    if (q.length < 2) {
      this.searchAbort?.abort();
      this.searchResults.innerHTML = this.searchRest;
      this.collectOptions();
      this.announce("");
      return;
    }

    if (this.searchCache.has(q)) {
      this.paint(this.searchCache.get(q));
      return;
    }

    // A slow response for "leg" must never overwrite a fast one for "legging".
    this.searchAbort?.abort();
    this.searchAbort = new AbortController();

    const root = window.Shopify?.routes?.root || "/";
    const url =
      root +
      "search/suggest?q=" +
      encodeURIComponent(q) +
      "&options[prefix]=last" +
      "&resources[type]=product,collection,article,page" +
      "&resources[limit]=6" +
      "&section_id=nexelete-predictive-search";

    try {
      const res = await fetch(url, { signal: this.searchAbort.signal });
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const frag = doc.querySelector("[data-nx-ps-results]");
      if (!frag) throw new Error("no results node");
      this.searchCache.set(q, frag.innerHTML);
      this.paint(frag.innerHTML);
    } catch (err) {
      if (err.name === "AbortError") return;
      // Leave the panel usable: the form still submits to /search on Enter.
      this.searchResults.innerHTML = "";
      this.collectOptions();
      this.announce("Search is unavailable right now. Press Enter for all results.");
    }
  }

  paint(html) {
    this.searchResults.innerHTML = html;
    this.collectOptions();
    const counter = this.searchResults.querySelector("[data-nx-ps-count]");
    const n = counter ? Number(counter.getAttribute("data-nx-ps-count")) : 0;
    this.announce(n === 0 ? "No results." : n + (n === 1 ? " result." : " results."));
  }

  announce(msg) {
    if (this.searchStatus) this.searchStatus.textContent = msg;
  }

  /* Options are re-collected after every paint. Ids are assigned here rather
     than in Liquid so they stay unique across the four resource groups. */
  collectOptions() {
    this.searchOpts = [...this.searchResults.querySelectorAll("[data-nx-ps-item]")];
    this.searchOpts.forEach((el, i) => {
      el.id = "nx-ps-opt-" + i;
      el.setAttribute("aria-selected", "false");
    });
    this.searchActive = -1;
    this.searchInput.setAttribute("aria-expanded", String(this.searchOpts.length > 0));
    this.searchInput.removeAttribute("aria-activedescendant");
  }

  moveActive(i) {
    this.searchOpts.forEach((el) => {
      el.setAttribute("aria-selected", "false");
      el.classList.remove("is-active");
    });
    this.searchActive = i;

    if (i < 0 || !this.searchOpts[i]) {
      this.searchInput.removeAttribute("aria-activedescendant");
      return;
    }
    const el = this.searchOpts[i];
    el.setAttribute("aria-selected", "true");
    el.classList.add("is-active");
    this.searchInput.setAttribute("aria-activedescendant", el.id);
    el.scrollIntoView({ block: "nearest" });
  }

  searchKeys(e) {
    if (e.key === "Escape") {
      // Escape clears a query first, and only closes an already-empty field.
      if (this.searchInput.value) {
        e.stopPropagation();
        this.searchInput.value = "";
        this.runSearch();
      }
      return;
    }

    if (!this.searchOpts.length) return;

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      // Wraps at both ends. -1 means nothing is active yet, so Down enters at
      // the top of the list and Up enters at the bottom.
      const n = this.searchOpts.length;
      const i = this.searchActive;
      this.moveActive(
        e.key === "ArrowDown" ? (i >= n - 1 ? 0 : i + 1) : (i <= 0 ? n - 1 : i - 1)
      );
      return;
    }

    if (e.key === "Enter" && this.searchActive > -1) {
      e.preventDefault();
      this.searchOpts[this.searchActive].click();
    }
  }

  /* ---------------------------------------------------------------- scroll */
  wireScroll() {
    // A zero-height sentinel is cheaper and smoother than a scroll listener:
    // the browser reports the crossing rather than us polling scrollY.
    // The sentinel must sit OUTSIDE the sticky wrapper — inside it, it would
    // stick too and never report a crossing.
    const sentinel = document.createElement("div");
    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText = "position:absolute;top:0;height:1px;width:1px;";
    document.body.insertBefore(sentinel, document.body.firstChild);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.setAttribute("data-stuck", String(!entry.isIntersecting));
      },
      { threshold: 0 }
    );
    this.observer.observe(sentinel);
  }
}

if (!customElements.get("nx-header")) {
  customElements.define("nx-header", NxHeader);
}
